import { AlertCircle, Calendar, Clock, Coffee, Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { BreakRuleInput, CustomScheduleInput, TimeIntervalInput } from "@/gql/graphql";

interface CustomScheduleBuilderProps {
  value: CustomScheduleInput;
  onChange: (value: CustomScheduleInput) => void;
}

const DAYS_OF_WEEK = [
  { value: 1, label: "Monday", short: "Mon" },
  { value: 2, label: "Tuesday", short: "Tue" },
  { value: 3, label: "Wednesday", short: "Wed" },
  { value: 4, label: "Thursday", short: "Thu" },
  { value: 5, label: "Friday", short: "Fri" },
  { value: 6, label: "Saturday", short: "Sat" },
  { value: 7, label: "Sunday", short: "Sun" },
];

const SCHEDULE_TYPES = [
  { value: "weekly", label: "Weekly", description: "Repeats every week" },
  {
    value: "biweekly_even",
    label: "Bi-weekly (Even weeks)",
    description: "Every other week starting with even weeks",
  },
  {
    value: "biweekly_odd",
    label: "Bi-weekly (Odd weeks)",
    description: "Every other week starting with odd weeks",
  },
];

const BREAK_TYPES = [
  { value: "REST", label: "Rest Break", description: "5-15 minutes" },
  { value: "BREAK", label: "Standard Break", description: "15-20 minutes" },
  { value: "MEAL", label: "Meal Break", description: "30-60 minutes" },
  { value: "PERSONAL", label: "Personal Time", description: "Personal break" },
];

export function CustomScheduleBuilder({ value, onChange }: CustomScheduleBuilderProps) {
  const updateSchedule = (updates: Partial<CustomScheduleInput>) => {
    onChange({ ...value, ...updates });
  };

  const toggleDay = (dayValue: number) => {
    const newDays = value.days.includes(dayValue)
      ? value.days.filter((d) => d !== dayValue)
      : [...value.days, dayValue].sort();
    updateSchedule({ days: newDays });
  };

  const addTimeInterval = () => {
    const newInterval: TimeIntervalInput = {
      startTime: "09:00",
      endTime: "17:00",
    };
    updateSchedule({
      timeIntervals: [...(value.timeIntervals || []), newInterval],
    });
  };

  const updateTimeInterval = (index: number, interval: TimeIntervalInput) => {
    const newIntervals = [...(value.timeIntervals || [])];
    newIntervals[index] = interval;
    updateSchedule({ timeIntervals: newIntervals });
  };

  const removeTimeInterval = (index: number) => {
    const newIntervals = [...(value.timeIntervals || [])];
    newIntervals.splice(index, 1);
    updateSchedule({ timeIntervals: newIntervals });
  };

  const addBreakRule = () => {
    const newBreak: BreakRuleInput = {
      afterHours: 4,
      breakType: "MEAL",
      duration: 30,
      isPaid: false,
      isRequired: true,
    };
    updateSchedule({
      breakRules: [...(value.breakRules || []), newBreak],
    });
  };

  const updateBreakRule = (index: number, breakRule: BreakRuleInput) => {
    const newBreaks = [...(value.breakRules || [])];
    newBreaks[index] = breakRule;
    updateSchedule({ breakRules: newBreaks });
  };

  const removeBreakRule = (index: number) => {
    const newBreaks = [...(value.breakRules || [])];
    newBreaks.splice(index, 1);
    updateSchedule({ breakRules: newBreaks });
  };

  const validateTimeInterval = (interval: TimeIntervalInput) => {
    return interval.startTime < interval.endTime;
  };

  const getTotalHours = () => {
    if (!value.timeIntervals || value.timeIntervals.length === 0) return 0;

    return value.timeIntervals.reduce((total, interval) => {
      const start = new Date(`2000-01-01 ${interval.startTime}`);
      const end = new Date(`2000-01-01 ${interval.endTime}`);
      const hours = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
      return total + hours;
    }, 0);
  };

  return (
    <div className="space-y-6">
      {/* Schedule Type */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Schedule Type
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <Select
            value={value.scheduleType}
            onValueChange={(scheduleType) => updateSchedule({ scheduleType })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SCHEDULE_TYPES.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  <div>
                    <div className="font-medium">{type.label}</div>
                    <div className="text-xs text-muted-foreground">{type.description}</div>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Days of Week */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Working Days</CardTitle>
          <CardDescription className="text-xs">
            Select which days this worker will be scheduled
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="flex flex-wrap gap-2">
            {DAYS_OF_WEEK.map((day) => (
              <Button
                key={day.value}
                variant={value.days.includes(day.value) ? "default" : "outline"}
                size="sm"
                onClick={() => toggleDay(day.value)}
                className="text-xs"
              >
                {day.short}
              </Button>
            ))}
          </div>
          {value.days.length === 0 && (
            <div className="flex items-center gap-2 mt-2 text-xs text-amber-600 dark:text-amber-400">
              <AlertCircle className="w-3 h-3" />
              <span>Please select at least one working day</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Time Intervals */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-sm flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Time Intervals
              </CardTitle>
              <CardDescription className="text-xs">
                Define work hours (supports split shifts)
              </CardDescription>
            </div>
            <div className="text-right">
              <div className="text-xs text-muted-foreground">Total Hours</div>
              <div className="font-medium text-sm">{getTotalHours().toFixed(1)}h</div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0 space-y-3">
          {value.timeIntervals?.map((interval, index) => (
            <div key={index} className="flex items-center gap-3 p-3 border rounded-lg">
              <div className="flex-1 grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor={`start-${index}`} className="text-xs">
                    Start Time
                  </Label>
                  <Input
                    id={`start-${index}`}
                    type="time"
                    value={interval.startTime}
                    onChange={(e) =>
                      updateTimeInterval(index, { ...interval, startTime: e.target.value })
                    }
                    className="h-8"
                  />
                </div>
                <div>
                  <Label htmlFor={`end-${index}`} className="text-xs">
                    End Time
                  </Label>
                  <Input
                    id={`end-${index}`}
                    type="time"
                    value={interval.endTime}
                    onChange={(e) =>
                      updateTimeInterval(index, { ...interval, endTime: e.target.value })
                    }
                    className="h-8"
                  />
                </div>
              </div>

              {!validateTimeInterval(interval) && (
                <AlertCircle
                  className="w-4 h-4 text-amber-500"
                  aria-label="End time must be after start time"
                />
              )}

              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeTimeInterval(index)}
                className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
              >
                <X className="w-3 h-3" />
              </Button>
            </div>
          ))}

          <Button variant="outline" size="sm" onClick={addTimeInterval} className="w-full text-xs">
            <Plus className="w-3 h-3 mr-1" />
            Add Time Interval
          </Button>

          {(!value.timeIntervals || value.timeIntervals.length === 0) && (
            <div className="flex items-center gap-2 text-xs text-amber-600 dark:text-amber-400">
              <AlertCircle className="w-3 h-3" />
              <span>Please add at least one time interval</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Break Rules */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-sm flex items-center gap-2">
                <Coffee className="w-4 h-4" />
                Break Rules
              </CardTitle>
              <CardDescription className="text-xs">
                Automatic breaks based on hours worked (optional)
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0 space-y-3">
          {value.breakRules?.map((breakRule, index) => (
            <div key={index} className="p-3 border rounded-lg space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium">Break {index + 1}</h4>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeBreakRule(index)}
                  className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                >
                  <X className="w-3 h-3" />
                </Button>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">After Hours</Label>
                  <Input
                    type="number"
                    min="0.5"
                    step="0.5"
                    max="12"
                    value={breakRule.afterHours}
                    onChange={(e) =>
                      updateBreakRule(index, {
                        ...breakRule,
                        afterHours: Number.parseFloat(e.target.value) || 0,
                      })
                    }
                    className="h-8"
                  />
                </div>
                <div>
                  <Label className="text-xs">Duration (minutes)</Label>
                  <Input
                    type="number"
                    min="5"
                    max="120"
                    value={breakRule.duration}
                    onChange={(e) =>
                      updateBreakRule(index, {
                        ...breakRule,
                        duration: Number.parseInt(e.target.value, 10) || 0,
                      })
                    }
                    className="h-8"
                  />
                </div>
              </div>

              <div>
                <Label className="text-xs">Break Type</Label>
                <Select
                  value={breakRule.breakType}
                  onValueChange={(breakType) => updateBreakRule(index, { ...breakRule, breakType })}
                >
                  <SelectTrigger className="h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {BREAK_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        <div>
                          <div className="font-medium text-xs">{type.label}</div>
                          <div className="text-xs text-muted-foreground">{type.description}</div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-4 text-xs">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={breakRule.isPaid || false}
                    onChange={(e) =>
                      updateBreakRule(index, { ...breakRule, isPaid: e.target.checked })
                    }
                    className="rounded"
                  />
                  <span>Paid break</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={breakRule.isRequired !== false}
                    onChange={(e) =>
                      updateBreakRule(index, { ...breakRule, isRequired: e.target.checked })
                    }
                    className="rounded"
                  />
                  <span>Required</span>
                </label>
              </div>
            </div>
          ))}

          <Button variant="outline" size="sm" onClick={addBreakRule} className="w-full text-xs">
            <Plus className="w-3 h-3 mr-1" />
            Add Break Rule
          </Button>
        </CardContent>
      </Card>

      {/* Summary */}
      <Card className="bg-muted/50">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Schedule Summary</CardTitle>
        </CardHeader>
        <CardContent className="pt-0 space-y-2 text-xs">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Schedule Type:</span>
            <span className="font-medium">
              {SCHEDULE_TYPES.find((t) => t.value === value.scheduleType)?.label}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Working Days:</span>
            <span className="font-medium">
              {value.days.length} days (
              {value.days.map((d) => DAYS_OF_WEEK.find((day) => day.value === d)?.short).join(", ")}
              )
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Daily Hours:</span>
            <span className="font-medium">{getTotalHours().toFixed(1)} hours</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Break Rules:</span>
            <span className="font-medium">{value.breakRules?.length || 0} rules</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
